// src/components/MaintenanceOeuvre.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { API_BASE_URL } from "../config/apiBase";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default function MaintenanceOeuvre() {
  const { artworkId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("historique");
  const [loading, setLoading] = useState(true);
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [artwork, setArtwork] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [newRecord, setNewRecord] = useState({
    date: new Date().toISOString().split("T")[0],
    type: "Restauration",
    contact: "",
    cost: "",
    notes: "",
  });
const [showDetailsModal, setShowDetailsModal] = useState(false);
const [selectedRecord, setSelectedRecord] = useState(null);

const showRecordDetails = (record) => {
  setSelectedRecord(record);
  setShowDetailsModal(true);
};

const getArtworkImageUrl = () => {
  return artwork?.image_url || "/images/artworks/Image_principale_Image_Not_Available.png";
};

const getFormattedDimensions = () => {
  if (!artwork?.height_cm || !artwork?.width_cm) return "";
  return `${artwork.height_cm} x ${artwork.width_cm} cm`;
};

const getArtworkTitle = () => {
  return artwork?.title || "Œuvre";
};

const downloadFile = async (path, name) => {
  const { data, error } = await supabase.storage.from("documents").download(path);
  if (error) {
    toast.error("Téléchargement échoué");
    return;
  }
  const url = window.URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
};



  const maintenanceTypes = [
    "Restauration",
    "Nettoyage",
    "Encadrement",
    "Inspection",
    "Transport",
    "Traitement préventif",
    "Autre"
  ];

  const fetchArtwork = useCallback(async () => {
    const { data, error } = await supabase.from("artworks").select("*").eq("id", artworkId).single();
    if (error) toast.error("Erreur chargement œuvre");
    else setArtwork(data);
  }, [artworkId]);

  const fetchMaintenanceRecords = useCallback(async () => {
    const { data, error } = await supabase.from("maintenance").select("*").eq("artwork_id", artworkId).order("date", { ascending: false });
    if (error) toast.error("Erreur chargement maintenance");
    else setMaintenanceRecords(data || []);
  }, [artworkId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchArtwork();
      await fetchMaintenanceRecords();
      setLoading(false);
    };
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artworkId]);

  const handleOcrFile = async (file) => {
    try {
      setOcrLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_BASE_URL}/ocr`, { method: "POST", body: formData });
      const data = await res.json();
      if (data.text) {
        const contactMatch = data.text.match(/Contact\s*[:-]\s*(.+)/i);
        const prestataireMatch = data.text.match(/(?:Émetteur|Entreprise|Prestataire|Fournisseur)\s*[:-]\s*(.+)/i);
        const contact = contactMatch ? contactMatch[1].trim() : "";
        const prestataire = prestataireMatch ? prestataireMatch[1].trim() : "";

        setNewRecord(prev => ({
          ...prev,
          contact: contact || prestataire || prev.contact,
          cost: data.suggestions?.cost || prev.cost,
          type: data.suggestions?.type || prev.type,
          notes: (prev.notes || "") + "\n[Pré-rempli OCR]\n" + data.text.slice(0, 300) + "..."
        }));
        toast.info("Champs pré-remplis depuis OCR");
      }
    } catch (err) {
      toast.error("Échec de l'analyse OCR");
    } finally {
      setOcrLoading(false);
    }
  };


  const totalMaintenanceCost = maintenanceRecords.reduce((sum, r) => sum + (r.cost || 0), 0);



  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { data, error } = await supabase.from("maintenance").insert({
        artwork_id: artworkId,
        date: newRecord.date,
        type: newRecord.type,
        contact: newRecord.contact,
        cost: parseFloat(newRecord.cost) || 0,
        notes: newRecord.notes,
      }).select();
      if (error) throw error;

      const newMaintenanceId = data[0].id;

      for (const file of uploadedFiles) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${fileExt}`;
        const filePath = `maintenance/${newMaintenanceId}/${fileName}`;
        const { error: uploadError } = await supabase.storage.from("documents").upload(filePath, file);
        if (uploadError) continue;
        await supabase.from("maintenance_files").insert({
          maintenance_id: newMaintenanceId,
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
          file_path: filePath,
        });
      }

      toast.success("Intervention enregistrée");
      setNewRecord({ date: new Date().toISOString().split("T")[0], type: "Restauration", contact: "", cost: "", notes: "" });
      setUploadedFiles([]);
      await fetchMaintenanceRecords();
      setActiveTab("historique");
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Rapport Maintenance - " + (artwork?.title || "Œuvre"), 20, 20);
    doc.setFontSize(12);
    doc.text(`Total interventions: ${maintenanceRecords.length}`, 20, 30);
    doc.text(`Total coût: ${totalMaintenanceCost.toLocaleString()} €`, 20, 38);
    doc.autoTable({
      startY: 50,
      head: [["Date", "Type", "Contact", "Coût (€)", "Notes"]],
      body: maintenanceRecords.map(r => [
        new Date(r.date).toLocaleDateString("fr-FR"),
        r.type,
        r.contact,
        (r.cost || 0).toLocaleString(),
        r.notes
      ])
    });
    doc.save("rapport_maintenance.pdf");
  };
const generateMaintenanceReport = exportPDF;
  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
<div className="flex justify-between items-center mb-8">
  <h1 className="text-3xl font-serif">Suivi de maintenance - {artwork?.title || "Œuvre"}</h1>
  <div className="flex space-x-4">
    <button
      onClick={() => navigate(`/artwork/${artworkId}`)}
      className="btn-artsy btn-secondary-artsy"
    >
      Retour à l'œuvre
    </button>
    <button
      onClick={generateMaintenanceReport}
      className="btn-artsy btn-primary-artsy"
      disabled={loading || maintenanceRecords.length === 0}
    >
      Générer un rapport
    </button>
  </div>
</div>
      
      <div className="flex flex-col md:flex-row gap-10">
        {/* Colonne gauche: image et info */}
        <div className="md:w-1/3">
          <div className="mb-6">
            <img
              src={getArtworkImageUrl()}
              alt={artwork?.title || "Œuvre d'art"}
              className="w-full object-contain"
              onError={(e) => {
                console.error("Erreur de chargement de l'image:", e);
                e.target.src = '/images/artworks/Image_principale_Image_Not_Available.png';
              }}
            />
          </div>
          
          <div className="mb-6">
            <h2 className="font-serif text-xl mb-3">{artwork?.title || "Sans titre"}</h2>
            <p className="text-gray-dark">{artwork?.artist || "Artiste inconnu"}{artwork?.year ? `, ${artwork.year}` : ""}</p>
            <p className="text-gray-dark">{artwork?.medium || "Technique non spécifiée"}</p>
            <p className="text-gray-dark">{getFormattedDimensions()}</p>
          </div>
          
          <div className="bg-gray-50 p-6 border border-gray-medium">
            <h3 className="font-medium mb-2">Résumé de maintenance</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span>Interventions totales:</span>
                <span className="font-medium">{maintenanceRecords.length}</span>
              </div>
              <div className="flex justify-between">
                <span>Coût total:</span>
                <span className="font-medium">{totalMaintenanceCost.toLocaleString()} €</span>
              </div>
              <div className="flex justify-between">
                <span>Dernière intervention:</span>
                <span className="font-medium">
                  {maintenanceRecords.length > 0 
                    ? new Date(maintenanceRecords[0].date).toLocaleDateString("fr-FR") 
                    : "Aucune"}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Colonne droite: contenu principal */}
        <div className="md:w-2/3">
          <div className="mb-6 border-b border-gray-medium">
            <div className="flex space-x-8">
              <button
                className={`py-3 text-sm ${activeTab === "historique" ? "font-medium border-b-2 border-blue-500" : "text-gray-dark"}`}
                onClick={() => setActiveTab("historique")}
              >
                Historique de maintenance
              </button>
              <button
                className={`py-3 text-sm ${activeTab === "ajouter" ? "font-medium border-b-2 border-blue-500" : "text-gray-dark"}`}
                onClick={() => setActiveTab("ajouter")}
              >
                Ajouter un enregistrement
              </button>
              <button
                className={`py-3 text-sm ${activeTab === "recommandations" ? "font-medium border-b-2 border-blue-500" : "text-gray-dark"}`}
                onClick={() => setActiveTab("recommandations")}
              >
                Recommandations
              </button>
            </div>
          </div>
          
          {/* Contenu de l'onglet Historique */}
          {activeTab === "historique" && (
            <div>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : maintenanceRecords.length > 0 ? (
                <div className="space-y-4">
                  {maintenanceRecords.map((record) => (
                    <div key={record.id} className="p-4 border border-gray-medium hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium text-lg">{record.type}</h3>
                          <p className="text-sm text-gray-dark">
                            {new Date(record.date).toLocaleDateString("fr-FR")}
                          </p>
                        </div>
                        <span className="font-medium">{(record.cost || 0).toLocaleString()} €</span>
                      </div>
                      <p className="text-sm mt-2 line-clamp-2">{record.notes}</p>
                      <div className="flex justify-between items-center mt-3">
                        <div className="flex items-center">
                          <span className="text-xs text-gray-dark mr-2">
                            {record.files?.length || 0} fichier(s) joint(s)
                          </span>
                        </div>
                        <button
                          onClick={() => showRecordDetails(record)}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          Voir détails
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 border border-gray-medium">
                  <p className="text-gray-dark">
                    Aucun enregistrement de maintenance disponible.
                  </p>
                  <button
                    onClick={() => setActiveTab("ajouter")}
                    className="mt-4 btn-artsy btn-primary-artsy text-sm"
                  >
                    Ajouter un enregistrement
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Contenu de l'onglet Ajouter */}
          {activeTab === "ajouter" && (
            <div>
              <h2 className="font-serif text-xl mb-4">Nouvel enregistrement de maintenance</h2>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="date" className="block text-sm font-medium text-gray-dark mb-1">
                      Date de maintenance
                    </label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={newRecord.date}
                      onChange={(e) => setNewRecord({...newRecord, date: e.target.value})}
                      required
                      className="w-full border border-gray-medium p-2"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-dark mb-1">
                      Type de maintenance
                    </label>
                    <select
                      id="type"
                      name="type"
                      value={newRecord.type}
                      onChange={(e) => setNewRecord({...newRecord, type: e.target.value})}
                      className="w-full border border-gray-medium p-2"
                    >
                      {maintenanceTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="contact" className="block text-sm font-medium text-gray-dark mb-1">
                      Prestataire / Contact
                    </label>
                    <input
                      type="text"
                      id="contact"
                      name="contact"
                      value={newRecord.contact}
                      onChange={(e) => setNewRecord({...newRecord, contact: e.target.value})}
                      placeholder="Nom de l'entreprise ou de la personne"
                      className="w-full border border-gray-medium p-2"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="cost" className="block text-sm font-medium text-gray-dark mb-1">
                      Coût (€)
                    </label>
                    <input
                      type="number"
                      id="cost"
                      name="cost"
                      value={newRecord.cost}
                      onChange={(e) => setNewRecord({...newRecord, cost: e.target.value})}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      className="w-full border border-gray-medium p-2"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-dark mb-1">
                      Notes et observations
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      rows="4"
                      value={newRecord.notes}
                      onChange={(e) => setNewRecord({...newRecord, notes: e.target.value})}
                      placeholder="Décrivez les interventions réalisées, l'état de l'œuvre, les recommandations..."
                      className="w-full border border-gray-medium p-2"
                    ></textarea>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-dark mb-1">
                      Fichiers joints
                    </label>
                    <div className="border border-dashed border-gray-medium p-4">
                      <div className="flex flex-col items-center">
                        <p className="text-sm text-gray-dark mb-2">
                          Ajoutez des factures, des photos, des rapports de restauration...
                        </p>
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          id="file-upload"
                          onChange={(e) => setUploadedFiles([...uploadedFiles, ...Array.from(e.target.files)])}
                        />
                        <label
                          htmlFor="file-upload"
                          className="btn-artsy btn-secondary-artsy text-sm cursor-pointer"
                        >
                          Sélectionner des fichiers
                        </label>
                      </div>
                      
                      {uploadedFiles.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm font-medium mb-2">Fichiers sélectionnés:</p>
                          <ul className="text-sm space-y-1">
                            {uploadedFiles.map((file, index) => (
                              <li key={index} className="flex items-center">
                                <span className="text-gray-dark">{file.name} ({(file.size / 1024).toFixed(0)} KB)</span>
                                <button
                                  type="button"
                                  className="ml-2 text-red-500 hover:text-red-700"
                                  onClick={() => {
                                    const newFiles = [...uploadedFiles];
                                    newFiles.splice(index, 1);
                                    setUploadedFiles(newFiles);
                                  }}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
{uploadedFiles.length > 0 && (
  <div className="mt-4 text-right">
    <button
      type="button"
      onClick={() => {
        const firstPdf = uploadedFiles.find(f => f.type === "application/pdf");
        if (firstPdf) handleOcrFile(firstPdf);
        else toast.info("Aucun fichier PDF détecté.");
      }}
      className="btn-artsy btn-secondary-artsy text-sm"
      disabled={ocrLoading}
    >
      {ocrLoading ? "Analyse OCR en cours..." : "Analyser le fichier (OCR)"}
    </button>
  </div>
)}

                    </div>
                  </div>
                </div>
                
                <div className="mt-8 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setActiveTab("historique")}
                    className="btn-artsy btn-secondary-artsy mr-2"
                    disabled={loading}
                  >
                    Annuler
                  </button>
                  <button 
                    type="submit" 
                    className="btn-artsy btn-primary-artsy"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <div className="animate-spin h-4 w-4 mr-2 border-t-2 border-b-2 border-white rounded-full"></div>
                        Enregistrement...
                      </span>
                    ) : (
                      "Enregistrer"
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
          
          {/* Contenu de l'onglet Recommandations */}
          {activeTab === "recommandations" && (
            <div>
              <h2 className="font-serif text-xl mb-4">Recommandations d'entretien</h2>
              
              <div className="space-y-6">
                <div className="bg-blue-50 p-4 border border-blue-200 rounded-md">
                  <h3 className="font-medium text-blue-800 mb-2">Fréquence d'inspection recommandée</h3>
                  <p className="text-sm">
                    Pour cette œuvre ({artwork?.medium || "technique non spécifiée"}), 
                    nous recommandons une inspection professionnelle tous les 12 mois.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-medium mb-3">Conditions environnementales optimales</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 border border-gray-medium">
                      <p className="font-medium text-sm">Température</p>
                      <p className="text-sm">18-22°C (64-72°F)</p>
                    </div>
                    <div className="bg-gray-50 p-4 border border-gray-medium">
                      <p className="font-medium text-sm">Humidité relative</p>
                      <p className="text-sm">45-55%</p>
                    </div>
                    <div className="bg-gray-50 p-4 border border-gray-medium">
                      <p className="font-medium text-sm">Luminosité</p>
                      <p className="text-sm">Maximum 150-200 lux</p>
                    </div>
                    <div className="bg-gray-50 p-4 border border-gray-medium">
                      <p className="font-medium text-sm">Exposition aux UV</p>
                      <p className="text-sm">Éviter l'exposition directe au soleil</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-medium mb-3">Entretien préventif</h3>
                  <ul className="list-disc pl-5 space-y-2 text-sm">
                    <li>Examiner régulièrement l'œuvre pour détecter des signes de détérioration.</li>
                    <li>Éviter de toucher directement la surface de l'œuvre.</li>
                    <li>Dépoussiérer délicatement avec un pinceau doux si nécessaire.</li>
                    <li>En cas de déplacement, manipuler avec des gants en coton ou en nitrile.</li>
                    <li>Pour le nettoyage, consulter un conservateur-restaurateur professionnel.</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-medium mb-3">Professionnels recommandés</h3>
                  <div className="space-y-3">
                    <div className="p-3 border border-gray-medium">
                      <p className="font-medium">Atelier de Restauration Beaux-Arts</p>
                      <p className="text-sm">42 Rue des Artisans, 75004 Paris</p>
                      <p className="text-sm">contact@atelierba.fr - 01 42 33 44 55</p>
                    </div>
                    <div className="p-3 border border-gray-medium">
                      <p className="font-medium">Marie Dupont, Conservatrice indépendante</p>
                      <p className="text-sm">15 Avenue Montaigne, 75008 Paris</p>
                      <p className="text-sm">m.dupont@conservation-art.fr - 06 11 22 33 44</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Modal des détails d'un enregistrement */}
      {showDetailsModal && selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
             onClick={() => setShowDetailsModal(false)}>
          <div className="bg-white p-8 rounded-md max-w-2xl w-full"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="font-serif text-xl">{selectedRecord.type}</h3>
                <p className="text-sm text-gray-dark">
                  Enregistré le {new Date(selectedRecord.date).toLocaleDateString("fr-FR")}
                </p>
              </div>
              <button onClick={() => setShowDetailsModal(false)} className="text-gray-500 hover:text-black">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-dark">Prestataire / Contact</p>
                  <p className="font-medium">{selectedRecord.contact || "Non spécifié"}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-dark">Coût</p>
                  <p className="font-medium">{(selectedRecord.cost || 0).toLocaleString()} €</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-dark mb-1">Notes et observations</p>
                <div className="p-3 bg-gray-50 border border-gray-medium rounded-md">
                  <p className="text-sm whitespace-pre-line">{selectedRecord.notes || "Aucune note"}</p>
                </div>
              </div>
              
              {selectedRecord.files && selectedRecord.files.length > 0 && (
                <div>
                  <p className="text-sm text-gray-dark mb-2">Fichiers joints</p>
                  <div className="space-y-2">
                    {selectedRecord.files.map((file, index) => (
                      <div key={index} className="p-2 flex justify-between items-center border border-gray-medium">
                        <span className="text-sm">{file.file_name}</span>
                        <button 
                          className="text-blue-600 text-sm hover:underline"
                          onClick={() => downloadFile(file.file_path, file.file_name)}
                        >
                          Télécharger
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="mt-8 flex justify-end space-x-3">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="btn-artsy btn-secondary-artsy"
              >
                Fermer
              </button>
              <button 
                className="btn-artsy btn-primary-artsy"
                onClick={() => {
                  // Créer un PDF pour cet enregistrement spécifique
                  try {
                    const doc = new jsPDF();
                    
                    // Titre
                    doc.setFontSize(18);
                    doc.text(`Détails de maintenance - ${getArtworkTitle()}`, 105, 20, { align: "center" });
                    
                    // Informations sur l'intervention
                    doc.setFontSize(14);
                    doc.text(`Type: ${selectedRecord.type}`, 20, 40);
                    doc.text(`Date: ${new Date(selectedRecord.date).toLocaleDateString("fr-FR")}`, 20, 50);
                    doc.text(`Contact: ${selectedRecord.contact || "Non spécifié"}`, 20, 60);
                    doc.text(`Coût: ${(selectedRecord.cost || 0).toLocaleString()} €`, 20, 70);
                    
                    // Notes
                    doc.setFontSize(14);
                    doc.text("Notes et observations:", 20, 90);
                    
                    // Ajouter les notes avec un retour à la ligne automatique
                    const splitNotes = doc.splitTextToSize(selectedRecord.notes || "Aucune note", 170);
                    doc.setFontSize(12);
                    doc.text(splitNotes, 20, 100);
                    
                    // Fichiers joints
                    if (selectedRecord.files && selectedRecord.files.length > 0) {
                      const yPos = 100 + (splitNotes.length * 7);
                      doc.setFontSize(14);
                      doc.text("Fichiers joints:", 20, yPos);
                      
                      doc.setFontSize(12);
                      selectedRecord.files.forEach((file, index) => {
                        doc.text(`${index + 1}. ${file.file_name}`, 20, yPos + 10 + (index * 8));
                      });
                    }
                    
                    // Pied de page
                    doc.setFontSize(10);
                    doc.text(`Document généré le ${new Date().toLocaleDateString("fr-FR")}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 10, { align: "center" });
                    
                    // Téléchargement du PDF
                    doc.save(`maintenance_${selectedRecord.type}_${new Date(selectedRecord.date).toLocaleDateString("fr-FR").replace(/\//g, "-")}.pdf`);
                    
                    toast.success("Le rapport détaillé a été généré avec succès");
                  } catch (error) {
                    console.error("Erreur lors de la génération du rapport détaillé:", error);
                    toast.error("Erreur lors de la génération du rapport détaillé");
                  }
                }}
              >
                Exporter PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}